import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

import type { UserRoleDetailsDto, UserRoleRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useSlug } from "@/hooks/useSlug";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { useDocumentQueries } from "@/queries/documents/queryOptions";
import { permissionMutations } from "@/queries/permissions/mutations";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { useServicePartnersQueries } from "@/queries/service-partners/queryOptions";
import { useTicketCategoryQueries } from "@/queries/ticket-categories/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import { RoleForm, type RoleFormProps } from "../components/RoleForm";
import { compileStructure, getDefaultFormValues, type RoleWizardStep } from "../components/roleFormManager";

export function CreateRolePage(): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const { state } = useLocation();
  const pageMeta = useMemo(() => (state == null ? {} : (state as CreateOrEditPageState)), [state]);
  const navigate = useNavigate();

  const communityGroupQueries = useCommunityGroupQueries();
  const permissionQueries = usePermissionQueries();
  const documentQueries = useDocumentQueries();
  const ticketCategoryQueries = useTicketCategoryQueries();
  const servicePartnersQueries = useServicePartnersQueries();

  const createRoleMutation = permissionMutations.useCreateRole();

  const queryArgs = {
    Limit: 10000,
  } satisfies ApiQueryParams<"getGroupsV2">;
  const {
    data: communityGroups = [],
    isFetching: isGroupsFetching,
    error: groupsError,
  } = useQuery({
    ...communityGroupQueries.list(queryArgs),
    select: (data) =>
      commonAPIDataSelector(data)
        .items.filter((group) => !group.isResidentGroup)
        .toSorted((a, b) => a.name.localeCompare(b.name)),
  });
  const roleId = pageMeta?.roleDetails?.id ?? pageMeta?.roleId;
  const roleIdForQuery = roleId ?? "";
  const {
    data: roleDetails,
    isFetching: isRoleDetailsFetching,
    error: roleDetailsError,
  } = useQuery({
    ...permissionQueries.roleDetails(roleIdForQuery),
    enabled: !!pageMeta?.roleId,
    initialData: pageMeta?.roleDetails,
  });
  const {
    data: folders = [],
    isFetching: isFoldersFetching,
    error: foldersError,
  } = useQuery({
    ...documentQueries.listRoots(),
    select: (data) => commonAPIDataSelector(data).rootFolders.toSorted((a, b) => a.name.localeCompare(b.name)),
  });
  const {
    data: ticketCategories = [],
    isFetching: isTicketCategoriesFetching,
    error: ticketCategoriesError,
  } = useQuery({
    ...ticketCategoryQueries.list(),
    select: (data) => commonAPIDataSelector(data).items,
  });
  const {
    data: servicePartners = [],
    isFetching: isServicePartnersFetching,
    error: servicePartnersError,
  } = useQuery({
    ...servicePartnersQueries.list(),
    select: (data) => commonAPIDataSelector(data).items.toSorted((a, b) => a.title.localeCompare(b.title)),
  });
  const isPageLoading =
    isGroupsFetching ||
    isRoleDetailsFetching ||
    isFoldersFetching ||
    isTicketCategoriesFetching ||
    isServicePartnersFetching;
  const defaultFormValues = useMemo(() => {
    if (isPageLoading) {
      return undefined;
    }

    return getDefaultFormValues(
      {
        communityGroups,
        ticketCategories: ticketCategories,
        servicePartners,
        folders,
      },
      roleDetails,
    );
  }, [isPageLoading, communityGroups, ticketCategories, servicePartners, folders, roleDetails]);

  const onSubmit: RoleFormProps["onSubmit"] = (data) => {
    const payload: UserRoleRequest = {
      ...data,
      permissions: {
        ...data.permissions,
        communityGroups: Object.values(data.permissions.communityGroups),
        repairCategories: Object.values(data.permissions.repairCategories),
        servicePages: Object.values(data.permissions.servicePages),
        folders: Object.values(data.permissions.folders),
      },
    };

    void createRoleMutation.mutateAsync(payload, {
      onSuccess: () => {
        void navigate(routes.roles.list({ slug }));
      },
    });
  };

  const structure = useMemo(
    () => compileStructure(t, communityGroups, ticketCategories, servicePartners, folders),
    [communityGroups, folders, ticketCategories, servicePartners, t],
  );

  const error = roleDetailsError || groupsError || ticketCategoriesError || servicePartnersError || foldersError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  return isPageLoading || defaultFormValues == null ? (
    <FullSizeLoader withPadding />
  ) : (
    <RoleForm
      onSubmit={onSubmit}
      defaultFormValues={defaultFormValues}
      isSubmitting={createRoleMutation.isPending}
      structure={structure}
      initialStepId={pageMeta?.initialStepId}
      isDuplicate={pageMeta?.roleId != null}
    />
  );
}

export type CreateOrEditPageState =
  | {
      roleId?: string;
      roleDetails?: UserRoleDetailsDto;
      initialStepId?: RoleWizardStep;
    }
  | undefined;
